import { defineStore } from 'pinia';
import { GetAnimalStatusReportService } from '@/modules/Entities/Shelter/modules/AnimalProfile/services';
import { API } from '@Providers';
import { INTAKE_TYPES } from '@IntakeModule';

interface State {
  animalHistoryKennelData: any[];
  animalStatusReport: any[];
  animalFields: any;
  animalsInGroups: any[];
  diagnosticTemplates: any[];
  examTemplates: any[];
  endpoint: string;
  followUp: any;
  fosterHistoryLoading: boolean;
  files: any;
  groups: any;
  groupPagination: any;
  groupPaginationParams: { page: number; limit: number } | null;
  groupEndpoint: string;
  history: any;
  isEditing: boolean;
  loading: boolean;
  medicalData: any;
  medicalStaff: any;
  medicationName: any;
  medicationTemplates: any[];
  pageData: any;
  procedureName: any;
  proceduresTemplates: any[];
  rabiesTag: string;
  strayHoldRemaining: number;
  surgeryTemplates: any[];
  tabPageLoading: boolean;
  templates: any;
  vaccinationTemplates: any[];
  voucherTemplates: any[];
  weightId: string;
}
export default defineStore('animalProfile', {
  state: (): State => ({
    animalHistoryKennelData: [],
    animalStatusReport: [],
    animalFields: [],
    animalsInGroups: [],
    diagnosticTemplates: [],
    examTemplates: [],
    endpoint: '',
    files: [],
    followUp: {},
    fosterHistoryLoading: false,
    groups: [],
    groupPagination: null,
    groupPaginationParams: null,
    groupEndpoint: '',
    history: [],
    isEditing: false,
    loading: false,
    medicalData: {},
    medicalStaff: [],
    medicationName: {},
    medicationTemplates: [],
    pageData: {},
    procedureName: '',
    proceduresTemplates: [],
    rabiesTag: '',
    strayHoldRemaining: 0,
    surgeryTemplates: [],
    tabPageLoading: false,
    templates: [],
    vaccinationTemplates: [],
    voucherTemplates: [],
    weightId: '',
  }),

  getters: {
    animal: (state) => state.pageData.animal,
    tempCart: (state) => (state.pageData.temp_cart?._id ? state.pageData.temp_cart : null),
    fosterCart: (state) => (state.pageData.foster_cart?._id ? state.pageData.foster_cart : null),
    shelterId: (state) => state.pageData?.animal?.shelter_id?.$oid,
    isInFoster: (state) => state.pageData.foster?.foster?.status === 'Active',
  },

  actions: {
    fetchPageDataService() {
      if (!this.endpoint) return;

      const endpoint = `/api/shelter/animal/get-animal-profile/${this.endpoint}`;

      return API({ endpoint }).then((result: any) => {
        this.pageData = result;
        this.animalFields = result.animalFields;
      });
    },

    fetchMedicalDataService() {
      if (!this.endpoint) return;
      this.tabPageLoading = true;

      const endpoint = `/api/shelter/medical/get-medical-data/${this.endpoint}`;

      return API({ endpoint })
        .then((result: any) => {
          this.medicalData = result;
        })
        .finally(() => (this.tabPageLoading = false));
    },

    async fetchGroupsService(
      type?: 'get-all' | 'get-animal-groups',
      pagination?: { page?: number; limit?: number; search?: string; sort?: string }
    ) {
      if (!this.groupEndpoint) return;
      this.tabPageLoading = true;

      const page = pagination?.page ?? (type === 'get-all' ? this.groupPaginationParams?.page : undefined);
      const limit = pagination?.limit ?? (type === 'get-all' ? this.groupPaginationParams?.limit : undefined);

      if (type === 'get-all' && (pagination?.page || pagination?.limit)) {
        this.groupPaginationParams = {
          page: page ?? 1,
          limit: limit ?? this.groupPaginationParams?.limit ?? 12,
        };
      }

      const queryParams: string[] = [];
      if (page) queryParams.push(`page=${page}`);
      if (limit) queryParams.push(`limit=${limit}`);
      if (pagination?.search) queryParams.push(`search=${encodeURIComponent(pagination.search)}`);
      if (pagination?.sort) queryParams.push(`sort=${pagination.sort}`);

      const queryString = queryParams.length ? `?${queryParams.join('&')}` : '';
      const endpoint = `/api/shelter/groups/${type || 'get-all'}/${this.groupEndpoint}${queryString}`;

      return API({ endpoint })
        .then((result: any) => {
          this.groups = result;
          this.groupPagination = result?.meta || null;
        })
        .finally(() => (this.tabPageLoading = false));
    },

    async fetchFilesDataService() {
      if (!this.endpoint) return;

      const endpoint = `/api/shelter/animal/get-animal-files/${this.endpoint}`;

      return API({ endpoint })
        .then((result: any) => {
          this.files = result.map(
            ({
              _id,
              collection,
              created_at,
              destinationPath,
              filename,
              path,
              type,
              spay_neuter,
              rabies,
              visibility,
            }: any) => ({
              _id: this.shelterId,
              collection,
              created_at,
              destinationPath,
              fileTypeId: _id,
              filename,
              icon: undefined,
              path,
              rabies,
              spay_neuter,
              type,
              visibility,
            })
          );
        })
        .finally(() => (this.tabPageLoading = false));
    },

    async fetchAnimalStatusReportData() {
      GetAnimalStatusReportService(this.animal._id.$oid).then((response) => {
        this.animalStatusReport = response?.data || [];
      });
    },

    // Files and medical are two views of the same records: the Files tab's certificate rows are
    // synthesized from surgeries and vaccinations, and medical uploads are listed there too. Refresh
    // both, or the Files tab and its counter drift out of sync with the medical panel.
    async refreshMedicalData() {
      this.tabPageLoading = true;

      await Promise.all([this.fetchMedicalDataService(), this.fetchFilesDataService()]).finally(
        () => (this.tabPageLoading = false)
      );
    },

    async refreshGroupData(type?: 'get-all' | 'get-animal-groups', pagination?: { page?: number; limit?: number }) {
      this.tabPageLoading = true;

      await this.fetchGroupsService(type, pagination)?.finally(() => (this.tabPageLoading = false));
    },

    async refreshData() {
      await this.fetchPageDataService();
    },

    async refreshFilesData() {
      this.tabPageLoading = true;

      await this.fetchFilesDataService().finally(() => (this.tabPageLoading = false));
    },

    async refreshAnimalStatusReportData() {
      this.tabPageLoading = true;

      await this.fetchAnimalStatusReportData().finally(() => (this.tabPageLoading = false));
    },

    getPersonLabel(item: any) {
      const formName = item?.intake?.form?.form_name || item?.form?.form_name;

      if (!formName) return 'Guardian';

      const isStray = formName === INTAKE_TYPES.STRAY;
      if (isStray) return 'Dropped Off By';

      // TODO AF 884 - After we create the tnvr add pet form we can filter here and display
      // the correct label/title
      // const is_tnvr = formName.toLowerCase() === INTAKE_TYPES.ADD_PET;
      // if (is_tnvr) return 'tnvr.title.colonyManager';

      const isFound = formName.toLowerCase() === INTAKE_TYPES.FOUND;
      if (isFound) return 'Found By:';

      return 'Guardian';
    },
  },
});
